import { createContext, useCallback, useContext, useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import { ApiError } from "../lib/api";
import { RIYAL, useI18n } from "../lib/i18n";
import type { MessageKey } from "../lib/messages";
import { useFavorites } from "../lib/favorites";
import { useRequireAuth } from "../lib/session";
import { IconAlert, IconBack, IconBolt, IconCheck, IconHeart, IconInfo, IconMinus, IconPlus, IconScan, IconShield, IconStar, IconVerified, IconWhatsApp, IconX } from "./icons";

// ---------------------------------------------------------------------------
// Page chrome
// ---------------------------------------------------------------------------
export function PageHeader({ title, back, actions }: { title: string; back?: boolean | string; actions?: ReactNode }) {
  const nav = useNavigate();
  const { t } = useI18n();
  useEffect(() => {
    document.title = `${title} · Walashy`;
  }, [title]);
  return (
    <div className="row" style={{ margin: "var(--s2) 0 var(--s4)", minHeight: 44 }}>
      {back && (
        <button className="icon-btn" aria-label={t("nav.back")} onClick={() => (typeof back === "string" ? nav(back) : nav(-1))} style={{ marginInlineStart: -10 }}>
          <IconBack />
        </button>
      )}
      <h1 style={{ fontSize: 28, flex: 1 }}>{title}</h1>
      {actions}
    </div>
  );
}

// ---------------------------------------------------------------------------
// States: every screen shows one of loading / error / empty / content.
// ---------------------------------------------------------------------------
export function Loading({ rows = 3 }: { rows?: number }) {
  const { t } = useI18n();
  return (
    <div aria-busy="true" aria-live="polite" className="stack">
      <span className="sr-only">{t("state.loading")}</span>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skeleton" style={{ height: i === 0 ? 120 : 64 }} />
      ))}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: ApiError | Error | null; onRetry?: () => void }) {
  const { t } = useI18n();
  return (
    <div className="state" role="alert">
      <IconAlert size={32} />
      <h2>{t("state.error")}</h2>
      <p>{error?.message}</p>
      {onRetry && (
        <button className="btn" onClick={onRetry}>
          {t("state.retry")}
        </button>
      )}
    </div>
  );
}

export function Empty({ title, body, action }: { title: string; body?: string; action?: ReactNode }) {
  return (
    <div className="state">
      <IconScan size={36} />
      <h2>{title}</h2>
      {body && <p>{body}</p>}
      {action}
    </div>
  );
}

export function Notice({ kind = "info", children }: { kind?: "info" | "warn" | "bad" | "good"; children: ReactNode }) {
  const Icon = kind === "good" ? IconCheck : kind === "info" ? IconInfo : IconAlert;
  return (
    <div className={`notice ${kind === "info" ? "" : kind}`} role={kind === "bad" ? "alert" : undefined}>
      <Icon size={20} />
      <div>{children}</div>
    </div>
  );
}

/** "Maker's warranty until Mar 2027" / "Seller warranty: 3 months". */
export function WarrantyText({ w }: { w: Warranty | undefined }) {
  const { t, date } = useI18n();
  if (!w) return null;
  return <>{w.type === "manufacturer" ? t("warranty.manufacturerUntil", { d: date(w.until!, { month: "short", year: "numeric" }) }) : t("warranty.sellerMonths", { n: w.months! })}</>;
}

// ---------------------------------------------------------------------------
// Toasts (confirm what just happened, using the same verb as the button)
// ---------------------------------------------------------------------------
const ToastCtx = createContext<(msg: string) => void>(() => {});
export function ToastProvider({ children }: { children: ReactNode }) {
  const [msg, setMsg] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const show = useCallback((m: string) => {
    setMsg(m);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setMsg(null), 3200);
  }, []);
  return (
    <ToastCtx.Provider value={show}>
      {children}
      <div aria-live="polite">{msg && <div className="toast">{msg}</div>}</div>
    </ToastCtx.Provider>
  );
}
export const useToast = () => useContext(ToastCtx);

// ---------------------------------------------------------------------------
// Form controls
// ---------------------------------------------------------------------------
export function Field({ label, hint, error, children, optional }: { label: string; hint?: string; error?: string | null; optional?: boolean; children: (id: string, describedBy?: string) => ReactNode }) {
  const id = useId();
  const { t } = useI18n();
  const hintId = hint ? `${id}-hint` : undefined;
  const errId = error ? `${id}-err` : undefined;
  return (
    <div className="field">
      <label htmlFor={id}>
        {label} {optional && <span className="muted small">({t("common.optional")})</span>}
      </label>
      {children(id, [hintId, errId].filter(Boolean).join(" ") || undefined)}
      {hint && <div id={hintId} className="hint">{hint}</div>}
      {error && <div id={errId} className="error">{error}</div>}
    </div>
  );
}

export function MoneyInput({ id, value, onChange, currency, describedBy, invalid }: { id?: string; value: number | null; onChange: (cents: number | null) => void; currency: string; describedBy?: string; invalid?: boolean }) {
  const [text, setText] = useState(value != null ? String(value / 100) : "");
  useEffect(() => {
    if (value != null && Math.round(Number(text) * 100) !== value) setText(String(value / 100));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <div className="input-group">
      <span className="addon">{currency === "SAR" ? RIYAL : currency}</span>
      <input
        id={id}
        className="input num"
        inputMode="decimal"
        dir="ltr"
        value={text}
        aria-describedby={describedBy}
        aria-invalid={invalid || undefined}
        onChange={(e) => {
          const raw = e.target.value.replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660)).replace(/[^\d.]/g, "");
          setText(raw);
          const n = Number(raw);
          onChange(raw && Number.isFinite(n) ? Math.round(n * 100) : null);
        }}
      />
    </div>
  );
}

export function QtyStepper({ value, min = 1, max, onChange, label }: { value: number; min?: number; max?: number; onChange: (n: number) => void; label: string }) {
  const clamp = (n: number) => Math.max(min, max ? Math.min(max, n) : n);
  return (
    <div className="stepper" role="group" aria-label={label}>
      <button type="button" aria-label="−" onClick={() => onChange(clamp(value - 1))} disabled={value <= min}>
        <IconMinus size={18} />
      </button>
      <input
        aria-label={label}
        inputMode="numeric"
        dir="ltr"
        value={value}
        onChange={(e) => {
          const n = parseInt(e.target.value.replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660)), 10);
          if (!Number.isNaN(n)) onChange(clamp(n));
        }}
      />
      <button type="button" aria-label="+" onClick={() => onChange(clamp(value + 1))} disabled={!!max && value >= max}>
        <IconPlus size={18} />
      </button>
    </div>
  );
}

export function Switch({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return <button type="button" role="switch" aria-checked={checked} aria-label={label} className="switch" onClick={() => onChange(!checked)} />;
}

export function Choice({ selected, onClick, title, hint, icon }: { selected: boolean; onClick: () => void; title: string; hint?: string; icon?: ReactNode }) {
  return (
    <button type="button" className="choice" aria-pressed={selected} onClick={onClick}>
      {icon}
      <strong>{title}</strong>
      {hint && <span>{hint}</span>}
    </button>
  );
}

/** Button that shows progress and prevents double submits. */
export function AsyncButton({ onClick, children, className = "btn primary", disabled, busyLabel }: { onClick: () => Promise<unknown>; children: ReactNode; className?: string; disabled?: boolean; busyLabel?: string }) {
  const [busy, setBusy] = useState(false);
  return (
    <button
      type="button"
      className={className}
      disabled={disabled || busy}
      aria-busy={busy}
      onClick={async () => {
        setBusy(true);
        try {
          await onClick();
        } finally {
          setBusy(false);
        }
      }}
    >
      {busy ? (
        <>
          <span className="spinner" aria-hidden /> {busyLabel ?? children}
        </>
      ) : (
        children
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Domain bits
// ---------------------------------------------------------------------------
/** Trade grade stamp (NEW, A–D / جديد، أ–د) with the plain-language condition for screen readers. */
export function Grade({ c, size }: { c: string | null; size?: "lg" }) {
  const { t } = useI18n();
  if (!c) return null;
  const label = t(`cond.${c}` as MessageKey);
  return (
    <span className={`grade g-${c} ${size ?? ""}`} title={label} aria-label={`${t("grade.label", { g: t(`grade.${c}` as MessageKey) })}: ${label}`}>
      {t(`grade.${c}` as MessageKey)}
    </span>
  );
}

/** Grade plus its meaning, used where condition is explained (listing page, condition step). */
export function CondTag({ c }: { c: string | null }) {
  const { t } = useI18n();
  if (!c) return null;
  return (
    <span className="grade-row">
      <Grade c={c} />
      <span className="small" style={{ fontWeight: 600 }}>{t(`cond.${c}` as MessageKey)}</span>
    </span>
  );
}

export type PublicSpec = { key: string; label: string; value: string; valueAr?: string; hex?: string; fromAboutScreen?: boolean };

/** Signature element: the device's own "About" screen, as a grouped list. */
export function SpecList({ specs, aiAssisted }: { specs: PublicSpec[]; aiAssisted?: boolean }) {
  const { t, locale } = useI18n();
  return (
    <section className="spec-list" aria-label={t("spec.aboutThisDevice")}>
      <div className="head">
        <span>{t("spec.aboutThisDevice")}</span>
        {aiAssisted && (
          <span className="row" style={{ gap: 4, color: "var(--green)" }} title={t("listing.aiAssisted")}>
            <IconScan size={16} /> {t("spec.fromAboutShort")}
          </span>
        )}
      </div>
      {specs.map((s) => (
        <div className="spec-row" key={s.key}>
          <span className="k">{t(`spec.${s.key}` as MessageKey) ?? s.label}</span>
          <span className="v" dir="auto">
            {s.hex && <span className="swatch" style={{ background: s.hex }} aria-hidden="true" />}
            {locale === "ar" && s.valueAr ? s.valueAr : s.value}
          </span>
          {s.fromAboutScreen && (
            <span className="src" title={t("spec.fromAbout")} aria-label={t("spec.fromAbout")}>
              <IconScan size={16} />
            </span>
          )}
        </div>
      ))}
    </section>
  );
}

export type Warranty = { type: "manufacturer" | "seller"; until: string | null; months: number | null } | null;

export type ListingSummary = {
  id: string;
  warranty?: Warranty;
  title: string;
  brand?: string;
  storageGb?: number | null;
  priceCents: number;
  newPriceCents?: number | null;
  savingsPct?: number | null;
  currency: string;
  condition: string;
  coverUrl: string | null;
  city: string;
  quantityAvailable: number;
  seller: { displayName: string; verified: boolean; rating: number | null; reviewCount: number; whatsapp?: boolean; phoneVerified?: boolean };
  quote?: { quantity: number; totalCents: number; deliveryFeeCents: number; fulfillment: string };
  priceVerdict?: Verdict | null;
  wasCents?: number | null;
  negotiable?: boolean;
};

// ---------------------------------------------------------------------------
// Price intelligence and trust: the reasons to buy here instead of anywhere else
// ---------------------------------------------------------------------------
export type Verdict = "great" | "fair" | "high";
export type PriceGuide = { lowCents: number; typicalCents: number; highCents: number; basis: { listings: number; deals: number; newPrice: boolean }; confidence: "high" | "medium" | "low" };

/** "Great price" / "Fair price" / "Above market", from the price guide. */
export function VerdictTag({ v, size }: { v: Verdict | null | undefined; size?: "sm" }) {
  const { t } = useI18n();
  if (!v) return null;
  return <span className={`verdict v-${v}${size === "sm" ? " sm" : ""}`}>{t(`verdict.${v}`)}</span>;
}

/**
 * The market band for this phone (25th–75th percentile, typical in the middle) with a marker for one price.
 * Mirrors in Arabic: low is always at the start of the reading direction.
 */
export function PriceGuideBar({ guide, priceCents, currency, label }: { guide: PriceGuide; priceCents?: number | null; currency: string; label?: string }) {
  const { t, money } = useI18n();
  const lo = guide.lowCents * 0.8;
  const hi = guide.highCents * 1.2;
  const pos = (c: number) => `${Math.max(0, Math.min(100, ((c - lo) / (hi - lo)) * 100))}%`;
  const basis =
    guide.basis.newPrice
      ? t("guide.basisNew")
      : t("guide.basis", { l: guide.basis.listings, d: guide.basis.deals });
  return (
    <div className="guide" role="group" aria-label={label ?? t("guide.title")}>
      <div className="guide-head">
        <span className="guide-title">{label ?? t("guide.title")}</span>
        <span className="guide-range num">{money(guide.lowCents, currency)} – {money(guide.highCents, currency)}</span>
      </div>
      <div className="guide-track" aria-hidden>
        <span className="band" style={{ insetInlineStart: pos(guide.lowCents), insetInlineEnd: `calc(100% - ${pos(guide.highCents)})` }} />
        <span className="typical" style={{ insetInlineStart: pos(guide.typicalCents) }} />
        {priceCents ? <span className="mark" style={{ insetInlineStart: pos(priceCents) }} /> : null}
      </div>
      <div className="guide-foot tiny muted">
        <span>{t("guide.typical", { p: money(guide.typicalCents, currency) })}</span>
        <span>{basis}{guide.confidence === "low" && !guide.basis.newPrice ? ` · ${t("guide.estimate")}` : ""}</span>
      </div>
    </div>
  );
}

export type Trust = {
  sales: number;
  purchases: number;
  rating: { average: number | null; count: number };
  replyMinutes: number | null;
  replyRate: number | null;
  phoneVerified: boolean;
  verifiedShop: boolean;
  memberSince: string;
  badges: ("verified_shop" | "verified_number" | "fast_reply" | "trusted")[];
};

const BADGE_ICON = { verified_shop: IconVerified, verified_number: IconShield, fast_reply: IconBolt, trusted: IconStar } as const;

/** Earned signals only, each from real activity on Walashy. */
export function TrustBadges({ trust, compact }: { trust: Trust; compact?: boolean }) {
  const { t } = useI18n();
  if (!trust.badges.length) return null;
  return (
    <ul className={`badges${compact ? " compact" : ""}`} aria-label={t("trust.badges")}>
      {trust.badges.map((b) => {
        const Icon = BADGE_ICON[b];
        return (
          <li key={b} className={`badge b-${b}`} title={t(`badge.${b}.hint`)}>
            <Icon size={14} /> {t(`badge.${b}`)}
          </li>
        );
      })}
    </ul>
  );
}

/** "Usually replies within 20 minutes · 3 sales · 4.8 ★" */
export function TrustLine({ trust }: { trust: Trust }) {
  const { t } = useI18n();
  const parts: string[] = [];
  if (trust.replyMinutes != null) parts.push(trust.replyMinutes < 60 ? t("trust.replyMin", { n: trust.replyMinutes }) : t("trust.replyHours", { n: Math.round(trust.replyMinutes / 60) }));
  if (trust.sales) parts.push(t("trust.sales", { n: trust.sales }));
  if (trust.rating.count) parts.push(t("listing.ratingLine", { avg: trust.rating.average ?? "", n: trust.rating.count }));
  if (!parts.length) parts.push(t("trust.new"));
  return <span>{parts.join(" · ")}</span>;
}

/** Saves or unsaves a listing. Sits on the photo, outside the card's link, so it is its own control. */
export function HeartButton({ id, title }: { id: string; title: string }) {
  const { t } = useI18n();
  const fav = useFavorites();
  const requireAuth = useRequireAuth();
  const toast = useToast();
  const saved = fav.has(id);
  return (
    <button
      type="button"
      className="heart"
      aria-pressed={saved}
      aria-label={`${saved ? t("listing.saved") : t("listing.save")}: ${title}`}
      onClick={() => requireAuth(() => { fav.toggle(id).catch(() => toast(t("state.error"))); })}
    >
      <IconHeart size={18} filled={saved} />
    </button>
  );
}

/** A phone in a grid or row: the photo on a grey tile, then name, condition, price and savings. */
export function ListingCard({ l, index = 0, compact }: { l: ListingSummary; index?: number; compact?: boolean }) {
  const { t, money } = useI18n();
  const multi = l.quote && l.quote.quantity > 1;
  return (
    <div className={`lcard${compact ? " compact" : ""}`} style={{ animationDelay: `${Math.min(index, 11) * 30}ms` }}>
      <Link to={`/l/${l.id}`} className="lcard-link">
        <div className="media">
          {l.coverUrl ? <img className="thumb" src={l.coverUrl} alt="" loading="lazy" /> : <div className="thumb" />}
        </div>
        <h3 className="title"><bdi>{l.title}</bdi></h3>
        <div className="cond"><Grade c={l.condition} /> {t(`cond.${l.condition}` as MessageKey)}</div>
        <div className="price">
          {money(l.priceCents, l.currency)}
          {l.wasCents ? <s className="was">{money(l.wasCents, l.currency)}</s> : null}
        </div>
        {multi && (
          <div className="total num">
            {t("browse.totalFor", { total: money(l.quote!.totalCents, l.currency), n: l.quote!.quantity })}
            {l.quote!.deliveryFeeCents > 0 && ` · ${t("browse.inclDelivery")}`}
          </div>
        )}
        <div className="card-meta">
          <VerdictTag v={l.priceVerdict} size="sm" />
          {!l.priceVerdict && l.savingsPct ? <span className="save">{t("card.saveVsNew", { p: l.savingsPct })}</span> : null}
          {l.warranty ? <span className="tiny muted"><IconCheck size={12} /> {t("warranty.badge")}</span> : null}
          {l.seller.whatsapp && <span className="wa" title={t("contact.whatsappOk")}><IconWhatsApp size={14} /><span className="sr-only">{t("contact.whatsappOk")}</span></span>}
        </div>
      </Link>
      <HeartButton id={l.id} title={l.title} />
    </div>
  );
}

/** "What do the grades mean?": a short sheet explaining A to D, opened wherever grades appear. */
export function GradeGuide() {
  const { t } = useI18n();
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  return (
    <>
      <button type="button" className="link" onClick={() => ref.current?.showModal()} aria-haspopup="dialog">{t("grades.what")}</button>
      <dialog ref={ref} className="sheet" aria-labelledby={titleId}>
        <div className="sheet-head">
          <button className="icon-btn" onClick={() => ref.current?.close()} aria-label={t("common.close")}><IconX /></button>
          <h2 id={titleId}>{t("grades.title")}</h2>
        </div>
        <div className="sheet-body">
          <p className="muted" style={{ marginBottom: "var(--s5)" }}>{t("grades.intro")}</p>
          <ul className="grade-list">
            {(["new", "like_new", "good", "fair", "for_parts"] as const).map((c) => (
              <li key={c}>
                <Grade c={c} size="lg" />
                <strong>{t(`cond.${c}`)}</strong>
                <p>{t(`cond.${c}.hint`)}</p>
              </li>
            ))}
          </ul>
        </div>
      </dialog>
    </>
  );
}

export function ConfirmDialog({ open, title, body, confirmLabel, onConfirm, onClose, tone = "primary" }: { open: boolean; title: string; body: ReactNode; confirmLabel: string; onConfirm: () => Promise<unknown>; onClose: () => void; tone?: "primary" | "offer" }) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const { t } = useI18n();
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog ref={ref} onClose={onClose} aria-labelledby={titleId} style={{ border: 0, borderRadius: "var(--r-xl)", padding: "var(--s5)", maxWidth: 440, width: "calc(100% - 32px)", background: "var(--bg)", color: "var(--ink)", boxShadow: "var(--shadow-sheet)" }}>
      <h2 id={titleId} style={{ fontSize: 20, margin: "0 0 var(--s3)" }}>{title}</h2>
      <div style={{ marginBottom: "var(--s5)" }}>{body}</div>
      <div className="stack">
        <AsyncButton className={`btn ${tone} block`} onClick={async () => { await onConfirm(); onClose(); }}>
          {confirmLabel}
        </AsyncButton>
        <button className="btn block ghost" onClick={onClose}>
          {t("common.cancel")}
        </button>
      </div>
    </dialog>
  );
}
